// src/utils/parseWebsiteLead.js

const MONTHS = {
  Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
  Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12',
};
const EXTRA_DRIVERS_PREFIX = 'extra_drivers:';
const MAX_EXTRA_DRIVERS = 10;

function normalizeExtraDriversCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(MAX_EXTRA_DRIVERS, Math.floor(n)));
}

function decodeHtmlEntities(s = '') {
  // handles &quot; etc if the email body is HTML-escaped
  if (!s) return '';
  // fast path for your sample:
  let out = s.replaceAll('&quot;', '"').replaceAll('&#34;', '"').replaceAll('&amp;', '&');
  // browser-safe decode if DOMParser exists
  if (typeof window !== 'undefined' && window.DOMParser) {
    try {
      const doc = new DOMParser().parseFromString(out, 'text/html');
      out = doc.documentElement.textContent || out;
    } catch {}
  }
  return out;
}

// "Mon, 01 Dec 2025, 11:00 UTC" -> Date (UTC)
function parsePrettyUtcDate(s) {
  if (!s) return null;
  const txt = s.trim();

  // try native first (sometimes works)
  const native = new Date(txt);
  if (!Number.isNaN(native.getTime())) return native;

  // robust parse: Day, DD Mon YYYY, HH:MM UTC
  const m = txt.match(/(\d{2})\s+([A-Za-z]{3})\s+(\d{4}),\s*(\d{2}):(\d{2})\s*UTC/i);
  if (!m) return null;

  const dd = m[1];
  const mon = MONTHS[m[2]] || '01';
  const yyyy = m[3];
  const hh = m[4];
  const mm = m[5];

  // Build ISO UTC
  const iso = `${yyyy}-${mon}-${dd}T${hh}:${mm}:00.000Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function uniqPhones(list) {
  const seen = new Set();
  const out = [];
  for (const p of list) {
    const v = (p || '').trim();
    if (!v) continue;
    const key = v.replace(/\s+/g, '');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v);
  }
  return out;
}

// Finds a carId from "Mercedes Benz Metris · FC100XO" using plate first, then name.
function matchCarIdFromSelectedCarLine(selectedCarLine, cars = []) {
  if (!selectedCarLine) return '';
  const line = selectedCarLine.trim();

  // take last token after dot/· as plate candidate
  const parts = line.split('·').map(x => x.trim());
  const plateCandidate = parts.length > 1 ? parts[parts.length - 1] : '';
  const nameCandidate = parts.length > 1 ? parts.slice(0, -1).join(' · ').trim() : line;

  const plateNorm = plateCandidate.replace(/\s+/g, '').toUpperCase();

  if (plateNorm) {
    const hitByPlate = cars.find(c => (c?.plate || '').replace(/\s+/g, '').toUpperCase() === plateNorm);
    if (hitByPlate?.id) return hitByPlate.id;
  }

  // fallback: name contains
  const nameNorm = nameCandidate.toLowerCase();
  const hitByName = cars.find(c => (c?.name || '').toLowerCase().includes(nameNorm));
  return hitByName?.id || '';
}

function parseExtrasJson(extrasText) {
  if (!extrasText) return [];

  const mappedExtras = [];
  let extraDriversCount = 0;

  const addExtra = (token) => {
    const v = (token || '').trim().toLowerCase();
    if (!v) return;
    if (!mappedExtras.includes(v)) mappedExtras.push(v);
  };

  const maybeReadExtraDrivers = (key, value) => {
    const k = String(key || '').trim().toLowerCase();
    if (!k) return false;

    if (
      k === 'extradriverscount' ||
      k === 'extra_drivers_count' ||
      k === 'extra_driver_count'
    ) {
      extraDriversCount = Math.max(extraDriversCount, normalizeExtraDriversCount(value));
      return true;
    }

    if (k === 'extradriver' || k === 'extra_driver' || k === 'extra_drivers') {
      if (value) extraDriversCount = Math.max(extraDriversCount, 1);
      return true;
    }

    const tokenMatch = k.match(/^extra[\s_-]*drivers?\s*[:=]\s*(\d+)$/i);
    if (tokenMatch) {
      extraDriversCount = Math.max(extraDriversCount, normalizeExtraDriversCount(tokenMatch[1]));
      return true;
    }

    return false;
  };

  const mapKeyToExtra = (key, value = true) => {
    if (maybeReadExtraDrivers(key, value)) return;

    if (!value) return;

    const normalized = String(key || '').trim().toLowerCase();
    if (!normalized) return;

    if (normalized === 'babyseat' || normalized === 'baby_seat' || normalized === 'babycarseat' || normalized === 'baby_car_seat') addExtra('baby_seat');
    else if (normalized === 'infantseat' || normalized === 'infant_seat') addExtra('infant_seat');
    else if (normalized === 'boosterseat' || normalized === 'booster_seat') addExtra('booster_seat');
    else if (normalized === 'snowchains' || normalized === 'snow_chains') addExtra('snow_chains');
    else if (normalized === 'crossborder' || normalized === 'cross_border') addExtra('cross_border');
    else if (normalized === 'gps') addExtra('gps');
    else if (normalized === 'bluetoothreceiver' || normalized === 'bluetooth_receiver') addExtra('bluetooth_receiver');
  };

  try {
    const parsed = JSON.parse(extrasText);

    if (Array.isArray(parsed)) {
      for (const item of parsed) {
        if (typeof item === 'string') {
          mapKeyToExtra(item, true);
          continue;
        }
        if (item && typeof item === 'object') {
          for (const [k, v] of Object.entries(item)) mapKeyToExtra(k, v);
        }
      }
    } else if (parsed && typeof parsed === 'object') {
      for (const [k, v] of Object.entries(parsed)) mapKeyToExtra(k, v);
    }

    if (extraDriversCount > 0) {
      addExtra(`${EXTRA_DRIVERS_PREFIX}${extraDriversCount}`);
    }

    return mappedExtras;
  } catch {
    return [];
  }
}

// Main: message text -> ReservationDrawer prefill
export function parseWebsiteMessageToPrefill(rawMessage, cars = []) {
  const text = decodeHtmlEntities(rawMessage || '');

  const getLine = (label) => {
    // grabs "Label: value" on one line
    const re = new RegExp(`${label}\\s*:\\s*([^\\n\\r]+)`, 'i');
    const m = text.match(re);
    return m ? m[1].trim() : '';
  };

  const name = getLine('Name');
  const email = getLine('Email');
  const phone = getLine('Phone');
  const whatsapp = getLine('WhatsApp');
  const startStr = getLine('Start');
  const endStr = getLine('End');
  const pickup = getLine('Pickup');
  const dropoff = getLine('Dropoff');
  const selectedCarLine = getLine('Selected car');
  const flight = getLine('Flight');

  // Extras line looks like: Extras: {"boosterSeat":false,...}
  let extrasRaw = getLine('Extras');
  extrasRaw = extrasRaw.trim();
  const extras = parseExtrasJson(extrasRaw);

  const pickupAt = parsePrettyUtcDate(startStr);
  const dropoffAt = parsePrettyUtcDate(endStr);

  const phones = uniqPhones([phone, whatsapp]);

  const carId = matchCarIdFromSelectedCarLine(selectedCarLine, cars);

  // Put whatever we can't store (email, full message) into notes so it isn't lost.
  const notesParts = [];
  if (email) notesParts.push(`Email: ${email}`);
  // If you want to keep original message (useful for audits)
  notesParts.push('');
  notesParts.push('--- Website lead ---');
  notesParts.push(text.trim());

  return {
    // ReservationDrawer create-mode will combine these with defaults and companyId from activeCompanyId/claims.
    pickupAt: pickupAt || undefined,     // Date preferred
    dropoffAt: dropoffAt || undefined,   // Date preferred
    pickupLocation: pickup || '',
    dropoffLocation: dropoff || '',
    customerName: name || '',
    flightNumber: flight || '',
    phoneNumbers: phones,               // drawer already handles arrays -> string
    extras,                             // ['baby_seat', ...]
    carId: carId || '',
    platform: 'website',
    title: name ? `Website - ${name}` : 'Website reservation',
    notes: notesParts.join('\n'),
  };
}
